import {getDocConfig, renderDownloadView, renderReadingView, viewIdentifiers} from "@/layouts/resources/utils";
import {animateScrollTo, autoPadNumber, notifyCatchedException, notifySentryError, Utils} from "@/modules/utils";
import Plugins from "@/modules/plugins";

interface FilteredDocsOptions {
    autoScroll: boolean,
    keepOldItems?: boolean,
    // oldItems?: NodeListOf<HTMLElement>
}

interface FilteredDocsResponse {
    state: boolean,
    total: number,
    loaded: number
    lastOldItem: HTMLElement | null
    pageItem: HTMLElement | null

}

const resourceListingListeners = () => {
    const view = document.querySelector(viewIdentifiers.listing);
    if (!view) return;

    let controller = new AbortController();
    const loadMoreHandler = view.querySelector<HTMLElement>('.load__more');
    const searchInput = view.querySelector<HTMLInputElement>('.search__wrapper input');
    const queryReset = view.querySelectorAll<HTMLInputElement>('[data-reset]');
    const headingContainer = view.querySelector<HTMLElement>('.heading__wrapper');

    const filtersContainer = view.querySelector<HTMLElement>('.filters__wrapper');
    const filterHandlerNode = filtersContainer.querySelector<HTMLElement>('.filter__handler');
    const filterHandler = filterHandlerNode.querySelector<SVGElement>('svg')

    const toggleFilters = (render: boolean) => {
        if (render) {
            filtersContainer.classList.add('render')
        } else {
            filtersContainer.classList.remove('render')
        }
    }

    const getFiltersOptions = () => filtersContainer.querySelectorAll<HTMLElement>('.filter__options .options div');

    const renderSelectedFilter = (el: HTMLElement | null, reset: boolean = false) => {
        const __set = (selectedElement: HTMLElement | null) => {
            if (!selectedElement) return;
            getFiltersOptions().forEach((e) => e.classList.remove('selected'));
            filterHandlerNode.querySelector('.selected__order').textContent = selectedElement.textContent.trim();
            selectedElement.classList.add('selected');
        }

        if (!reset) {
            if (el) __set(el)
            else {
                const el = filtersContainer.querySelector<HTMLElement>('.filter__options .options .selected');
                if (!el) __set(getFiltersOptions()[0]);
                else __set(el);
            }
        } else __set(getFiltersOptions()[0])
    }

    // filterHandler.addEventListener('mouseleave', () => toggleFilters(false));
    // filterHandler.addEventListener('mouseenter', () => toggleFilters(true));

    filterHandler.addEventListener('click', () => {
        toggleFilters(!filtersContainer.classList.contains('render'))
    });

    document.addEventListener('click', (e) => {
        if (!(e.target as any).closest('.filters__wrapper')) toggleFilters(false)
    })


    const registerItemsListeners = () => {

        // Old listeners Cleanup
        controller?.abort();
        controller = new AbortController();
        const {signal} = controller;

        document.querySelectorAll<HTMLElement>('.resource__card').forEach((el) => {

            //handle in-view read click
            el.querySelectorAll<HTMLElement>('[data-read]').forEach((readBtn) => {
                readBtn.addEventListener('click', () => renderReadingView(el, true), {signal})
            });

            //handle standalone view read click
            el.querySelectorAll<HTMLElement>('[data-open]').forEach((readBtn) => {
                readBtn.addEventListener('click', () => renderReadingView(el, false), {signal})
            });

            //handle download click
            const downloadCta = el.querySelector('.download__cta')
            const downloadHandler = downloadCta.querySelector('svg');

            downloadHandler.addEventListener('mouseleave', () => downloadCta.classList.remove('render'), {signal});
            downloadHandler.addEventListener('mouseenter', () => downloadCta.classList.add('render'), {signal});
            downloadHandler.addEventListener('click', () => {
                const config = getDocConfig(el);
                renderDownloadView(config)
            }, {signal});
        });
    }

    const buildFiltersRoute = (key: string, value: string) => {
        const initialRoute = view.getAttribute('data-aync-route');
        const route = new URL(initialRoute);
        route.searchParams.set('order_by', key);
        route.searchParams.set('order_dir', value);

        const finalRoute = route.toString();
        view.setAttribute('data-aync-route', finalRoute);
        return finalRoute;
    }

    const buildSearchRoute = (value: string) => {
        const initialRoute = view.getAttribute('data-aync-route');
        const route = new URL(initialRoute);
        route.searchParams.set('page', '1');
        route.searchParams.set('q', value);
        const finalRoute = route.toString();
        view.setAttribute('data-aync-route', finalRoute);
        return finalRoute;
    }

    const resetFilters = () => {
        const route = new URL(view.getAttribute('data-aync-route'));
        route.search = ''
        route.searchParams.set('page', '1');
        const finalRoute = route.toString();

        searchInput.value = '';
        searchInput.removeAttribute('data-state');

        renderSelectedFilter(null, true);
        toggleFilters(false);

        view.setAttribute('data-aync-route', finalRoute);
        renderFilteredDocs(finalRoute, {autoScroll: true}).then(x => x);
    }

    queryReset.forEach((el) => {
        el.addEventListener('click', () => resetFilters())
    })

    const buildSkeleton = (count = 12) => {
        const fragment = document.createDocumentFragment();

        for (let i = 0; i < count; i++) {
            const div = document.createElement('div');
            div.classList.add('resource__card__skeleton');
            const img = document.createElement('img')
            img.setAttribute('src', Utils.getShimmer());
            img.setAttribute('alt', 'skeleton');
            div.appendChild(img);
            fragment.appendChild(div);
        }

        return fragment;
    }

    const toggleLoadMore = (render: boolean) => {
        loadMoreHandler.style.display = render ? 'block' : 'none';
    }

    const renderFilteredDocs = (route: string, {autoScroll, keepOldItems}: FilteredDocsOptions): Promise<FilteredDocsResponse> => {
        return new Promise((resolve, reject) => {

            const container = view.querySelector('.resources__items');
            const counter = view.querySelectorAll<HTMLElement>('.items__counter');

            toggleLoadMore(false);

            const oldItemsHTML = keepOldItems ? container.innerHTML : '';
            const currentItems = container.querySelectorAll('.resource__card');
            const lastItemRef = currentItems[currentItems.length - 1]?.getAttribute('data-doc') || '';

            container.replaceChildren();
            container.appendChild(buildSkeleton(currentItems?.length || 12));

            if (autoScroll) animateScrollTo(headingContainer, 130);

            fetch(route).then((response) => response.json()).then((data) => {
                /**
                 * @param {Record<string, string|number>} data
                 * @example: {"html": "<><>", "total": 6, "page": 1, "perPage": 12, "orderBy": "name", "orderDir": "asc", "loaded": 6}
                 */

                    // console.log(data)

                const page = parseInt(data.page);
                const perPage = parseInt(data['perPage']);

                const loaded = parseInt(data.loaded);
                const total = parseInt(data.total);

                const route = new URL(view.getAttribute('data-aync-route'));
                route.searchParams.set('page', page.toString());
                route.searchParams.set('per_page', perPage.toString());
                view.setAttribute('data-aync-route', route.toString());

                setTimeout(() => {
                    counter.forEach((ctx) => {
                        ctx.querySelectorAll<HTMLElement>('.loaded').forEach((el) => {
                            el.textContent = String(loaded).padStart(3, '0');
                        })

                        ctx.querySelectorAll<HTMLElement>('.total').forEach((el) => {
                            el.textContent = String(total).padStart(3, '0');
                        })
                    })

                    toggleLoadMore(total > 0 && loaded < total);

                    const pageDivider = page > 1 ? `<div class="page__divider" data-page="${page}"><hr/> <span>Page ${autoPadNumber(page)}</span> <hr/> </div>` : ''

                    container.replaceChildren();
                    container.innerHTML = `${oldItemsHTML}${pageDivider}${data['html']}`;

                    const lastItem = document.querySelector<HTMLElement>(`[data-doc="${lastItemRef}"]`);
                    const lastPage = document.querySelector<HTMLElement>(`[data-page="${page}"]`);
                    registerItemsListeners();

                    if (autoScroll) animateScrollTo(headingContainer, 130);
                    Plugins.initAos('hard');

                    resolve({
                        state: true,
                        total: total,
                        loaded: loaded,
                        pageItem: lastPage,
                        lastOldItem: lastItem,
                    });
                }, 1000);
            }).catch((error) => {
                notifyCatchedException(error);
                console.log(error)
                resolve({state: false, loaded: 0, total: 0, lastOldItem: null, pageItem: null});
                reject({state: false, loaded: 0, total: 0, lastOldItem: null, lastPage: null});
            })
        })

    }

    renderSelectedFilter(null);

    getFiltersOptions().forEach((el) => {
        el.addEventListener('click', () => {
            filterHandlerNode.querySelector('.selected__order').textContent = el.textContent.trim();
            toggleFilters(false);
            renderSelectedFilter(el);

            const value = el.getAttribute('data-order').split(',');
            const route = buildFiltersRoute(value[0], value[1])
            renderFilteredDocs(route, {autoScroll: true,}).then(x => x)
        })
    })

    const __fireSearch = (value: string) => {
        searchInput.setAttribute('data-state', 'least-filled');
        renderFilteredDocs(buildSearchRoute(value), {autoScroll: true}).then(x => x)
    }

    const SEARCH_DELAY_MS = 500;
    const MIN_QUERY_LENGTH = 2;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const prepareSearch = (query: string, immediate: boolean): void => {
        const trimmed = query.trim();

        if (debounceTimer) clearTimeout(debounceTimer);
        if (trimmed.length > 0) {
            searchInput.setAttribute('data-state', 'filled')
            if (trimmed.length >= MIN_QUERY_LENGTH) {
                if (immediate) __fireSearch(trimmed)
                else debounceTimer = setTimeout(() => __fireSearch(trimmed), SEARCH_DELAY_MS);
            }
        } else searchInput.removeAttribute('data-state');
    };

    // 1. Debounced search on every input change
    searchInput.addEventListener('input', (e) => {
        const value = (e.target as HTMLInputElement).value;
        prepareSearch(value, false);
    });

    // 2. Immediate search on Enter (cancels the debounce)
    searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const value = searchInput.value.trim();
            prepareSearch(value, true);
        }
    });

    loadMoreHandler.addEventListener('click', () => {
        const container = view.querySelector<HTMLElement>('.resources__items');
        container.style.height = `${container.clientHeight}px`;

        const route = new URL(view.getAttribute('data-aync-route'));
        const currentPage = parseInt(route.searchParams.get('page') || '0');
        route.searchParams.set('page', (currentPage + 1).toString())

        renderFilteredDocs(route.toString(), {autoScroll: false, keepOldItems: true}).then(({loaded, total, pageItem, lastOldItem}) => {
            container.style.height = 'auto';
            console.log({loaded, total});

            // Notify.info(`${autoPadNumber(loaded, total.toString().length)} sur ${total} ressources chargées`);
            if (!pageItem || !lastOldItem) console.warn('Last item or Last Page Element may be not found');
            animateScrollTo(pageItem || lastOldItem, 120, 1000);
        })
    })

    registerItemsListeners();
}

export default resourceListingListeners;